"use client";

import { useModal } from "@/hooks/useModal/useModal";
import { IHotelOrCity } from "@/types/availableRoom";
import { valibotResolver } from "@hookform/resolvers/valibot";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as v from "valibot";
import {
  GroupOption,
  SelectOption,
} from "../../search-avaliable/SearchAvailableRoom";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { getPublicEnv } from '@/utils/env';
import {
  API_RESPONSE_STATUS,
  datePickerFormat,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import ContactForm from "./ContactInforForm";
import MeetingEventForm from "./MeetingEventForm";
import {
  ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel,
  MessageDatasource,
} from "@/types/generated";
import { iconList } from "@/assets/icons";
import { format } from "date-fns";
import { getValidationMessagse } from "@/utils";
import {
  useGetFormLabelsQuery,
  useGetValidationMessagesQuery,
} from "@/hooks/useMiceFormQueries/useMiceFormQueries";
import {
  EValidationMessageKey,
  IApiResponse,
  ESubmitStatus,
} from "@/types/common";
import "../../hotel-details/meeting-room/meeting-icomoon-bundle.css";
import api from "@/lib/api";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import { Button } from "@/components/atoms/button";
import { Modal } from "@/components/atoms/modal";
interface IContactFormModalProps {
  contactBtn: string;
  contactBtnIcon?: string;
  hotelId?: string;
  variantTriggerBtn?: "primary" | "secondary" | "error" | "neutral" | null;
  hierarchyTriggerBtn?: "filled" | "tonal" | "outline" | "ghost" | null;
  sizeTriggerBtn?: "sm" | "lg" | "xl" | "xs" | "md" | null;
  classNameTriggerBtn?: string;
  /** When set, replaces the default Button trigger (e.g. Art-styled contact CTA). */
  customTrigger?: React.ReactNode;
}

export type IContactForm = {
  selectedLocation: {
    value: string;
    label: string;
  };
  firstName: string;
  lastName: string;
  companyName: string;
  emailAddress: string;
  phone: string;
  eventName: string;
  noOfAttendees: string;
  startDate: Date | null;
  endDate: Date | null;
  rooms?: string;
  duration?: {
    value: string;
    label: string;
  } | null;
  typeOfEvent?: {
    value: string;
    label: string;
  } | null;
  hasBreakfast?: boolean;
  hasLunch?: boolean;
  hasDinner?: boolean;
  hasReception?: boolean;
  hasOther?: boolean;
  attachFiles?: File[] | null;

  requirement?: string;
};

export interface MeetingMessagePayload {
  selectedLocation: string;
  selectedHotelId: string;
  firstName: string;
  lastName: string;
  company: string;
  emailAddress: string;
  phone: string;
  eventName: string;
  startDate: string;
  endDate: string;
  noOfAttendees: string;
  rooms?: string;
  duration?: string;
  typeOfEvent?: string;
  requirement?: string;
  hasBreakfast?: boolean;
  hasLunch?: boolean;
  hasDinner?: boolean;
  hasReception?: boolean;
  hasOther?: boolean;
  attachments?: string[];
}
/**
 * ContactFormModal Component
 *
 * This component provides a complete contact form for event or meeting inquiries.
 * It includes:
 * - Modal handling (open/close state)
 * - Dynamic form schema validation using Zod and CMS-fetched validation messages
 * - File upload support
 * - Multi-step submission flow (loading, success, error)
 * - Integration with global Japan areas for hotel selection
 *
 * Comments are provided for sections where logic is complex or non-trivial.
 */
/**
 * Form label and validation message queries are provided by shared hooks
 * extracted to `src/hooks/useMiceFormQueries.ts` to avoid duplication.
 */

/** Build Zod validation schema dynamically based on CMS validation messages */
const ContactFormModal = ({
  hotelId,
  contactBtn,
  contactBtnIcon,
  variantTriggerBtn = "secondary",
  hierarchyTriggerBtn = "filled",
  sizeTriggerBtn = "lg",
  classNameTriggerBtn = "w-full xl:w-fit",
  customTrigger,
}: IContactFormModalProps) => {
  const { Spinner } = iconList;
  const { data: formLabelResponse } = useGetFormLabelsQuery();
  const { data: validationMessagesResponse } = useGetValidationMessagesQuery();

  const formLabel = (formLabelResponse
    ?.sharedDatasourcesReusableLightBoxLightBoxDatasource?.labels ||
    {}) as ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel;

  const validationMessages = (validationMessagesResponse?.messageDatasources ||
    []) as MessageDatasource[];

  const schemaContactForm = v.object({
    selectedLocation: v.object({
      label: v.pipe(
        v.string(),
        v.minLength(1, getValidationMessagse(
          EValidationMessageKey.REQUIRE,
          validationMessages,
        ))
      ),
      value: v.pipe(
        v.string(),
        v.minLength(1, getValidationMessagse(
          EValidationMessageKey.REQUIRE,
          validationMessages,
        ))
      ),
    }),
    firstName: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      ))
    ),
    lastName: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      ))
    ),
    companyName: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      ))
    ),
    emailAddress: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      )),
      v.email(getValidationMessagse(
        EValidationMessageKey.EMAIL_FORMAT,
        validationMessages,
      ))
    ),
    phone: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      )),
      v.regex(/^(\+?[1-9]\d{1,14}|0\d{1,4}-\d{1,4}-\d{1,4}|0\d{9,11})$/, getValidationMessagse(
        EValidationMessageKey.PHONE_FORMAT,
        validationMessages,
      ))
    ),
    eventName: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      ))
    ),
    noOfAttendees: v.pipe(
      v.string(),
      v.minLength(1, getValidationMessagse(
        EValidationMessageKey.REQUIRE,
        validationMessages,
      )),
      v.regex(/^\d+$/, getValidationMessagse(
        EValidationMessageKey.POSITIVE_NUMBER_FORMAT,
        validationMessages,
      ))
    ),
    startDate: v.union([v.date(), v.null()]),
    endDate: v.union([v.date(), v.null()]),
    rooms: v.optional(
      v.pipe(
        v.string(),
        v.regex(/^\d+$/, getValidationMessagse(
          EValidationMessageKey.POSITIVE_NUMBER_FORMAT,
          validationMessages,
        ))
      )
    ),
    duration: v.optional(
      v.union([
        v.object({
          label: v.string(),
          value: v.string(),
        }),
        v.null()
      ])
    ),
    typeOfEvent: v.optional(
      v.union([
        v.object({
          label: v.string(),
          value: v.string(),
        }),
        v.null()
      ])
    ),
    hasBreakfast: v.optional(v.boolean()),
    hasLunch: v.optional(v.boolean()),
    hasDinner: v.optional(v.boolean()),
    hasReception: v.optional(v.boolean()),
    hasOther: v.optional(v.boolean()),
    requirement: v.optional(v.string()),
    attachFiles: v.optional(v.union([v.array(v.instance(File)), v.null()])),
  });
  const { japanAreas } = useJapanAreasStore();
  const {
    submit_label,
    thank_you_message_text,
    free_quotation_text,
    terms_and_conditions_text,
  } =
    formLabel ||
    ({} as ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel);
  const [submitStatus, setSubmitStatus] = useState<ESubmitStatus>(
    ESubmitStatus.IDLE,
  );

  /** Builds a grouped list of hotels for the location selector */
  const listOption = (japanAreas || [])?.reduce((groups, area) => {
    const hotelGroup = {
      label: "ホテル",
      options: [] as SelectOption[],
    };
    if (area?.hotels) {
      for (const hotel of area.hotels) {
        if (hotel) {
          hotelGroup.options.push({
            label: hotel?.properties?.hotel_name || hotel?.item_name || "",
            value: hotel?.documentId ?? "",
          });
        }
      }
    }

    // Merge with existing hotel group if present
    if (hotelGroup?.options?.length > 0) {
      const existingHotelGroup = groups.find(
        (g) => g.label === "ホテル" || g.label === "Hotel",
      );
      if (existingHotelGroup) {
        existingHotelGroup.options = [
          ...existingHotelGroup.options,
          ...hotelGroup.options,
        ];
      } else {
        groups.push(hotelGroup);
      }
    }

    return groups;
  }, [] as GroupOption[]);

  const defaultValues: IContactForm = {
    selectedLocation: React.useMemo(() => {
      if (!hotelId) return {} as IHotelOrCity;

      if (
        Array.isArray(listOption) &&
        listOption.length > 0 &&
        "value" in listOption[0]
      ) {
        const foundOption = listOption
          .flatMap((group) => group.options)
          .find((option) => option.value === hotelId);
        if (foundOption) return foundOption;
      } else if (
        Array.isArray(listOption) &&
        listOption.length > 0 &&
        "options" in listOption[0]
      ) {
        const groups = listOption as {
          label: string;
          options: SelectOption[];
        }[];
        for (const group of groups) {
          const foundOption = group.options.find(
            (option) => option.value === hotelId,
          );
          if (foundOption) return foundOption;
        }
      }

      return {} as IHotelOrCity;
    }, [hotelId, listOption]),
    firstName: "",
    lastName: "",
    companyName: "",
    emailAddress: "",
    phone: "",
    eventName: "",
    noOfAttendees: "",
    startDate: new Date(DEFAULT_CHECKIN_DATE),
    endDate: new Date(DEFAULT_CHECKOUT_DATE),
    rooms: undefined,
    duration: null,
    typeOfEvent: null,
    hasBreakfast: false,
    hasLunch: false,
    hasDinner: false,
    hasReception: false,
    hasOther: false,
    requirement: "",
    attachFiles: null,
  };
  const methods = useForm<IContactForm>({
    defaultValues,
    resolver: valibotResolver(schemaContactForm),
  });
  const { handleSubmit, reset } = methods;
  const { isOpen, toggleModal } = useModal();

  /**
   * Handles form submission:
   * - Uploads attachments
   * - Constructs payload
   * - Sends email via Strapi endpoint
   * - Updates submission state (success/error)
   */
  const onSubmit = async (data: IContactForm) => {
    try {
      const {
        attachFiles,
        selectedLocation,
        startDate,
        endDate,
        companyName,
        rooms,
        duration,
        typeOfEvent,
      } = data;
      setSubmitStatus(ESubmitStatus.LOADING);
      let attachmentIds: string[] = [];
      if (attachFiles) {
        for (const file of attachFiles) {
          const formData = new FormData();
          formData.append("files", file);
          formData.append("name", file.name);

          try {
            const response = await api.post(
              `${getPublicEnv('NEXT_PUBLIC_STRAPI_API_URL')}/upload`,
              formData,
            );

            if (
              response.status === API_RESPONSE_STATUS.SUCCESS ||
              response.status === API_RESPONSE_STATUS.CREATED
            ) {
              const data = (response?.data as { documentId: string }[])?.[0]
                ?.documentId;
              attachmentIds = [...attachmentIds, data];
            } else {
              console.error("Failed to upload file. Please try again.");
            }
          } catch (error) {
            console.error("Error uploading file:", error);
          }
        }
      }
      const payload: MeetingMessagePayload = {
        ...data,
        selectedLocation: selectedLocation.label,
        selectedHotelId: selectedLocation.value,
        startDate: startDate
          ? format(new Date(startDate), datePickerFormat)
          : "",
        endDate: endDate ? format(new Date(endDate), datePickerFormat) : "",
        attachments: attachmentIds,
        company: companyName,
        rooms: rooms?.toString() || "",
        duration: duration?.value || "",
        typeOfEvent: typeOfEvent?.value || "",
      };
      await api.post<IApiResponse<string>>(
        `${getPublicEnv('NEXT_PUBLIC_STRAPI_API_URL')}/email/meeting-message-send-mail`,
        payload,
      );
      setSubmitStatus(ESubmitStatus.SUCCESS);
      reset(defaultValues);
    } catch (err) {
      setSubmitStatus(ESubmitStatus.ERROR);
      console.error("Failed to submit form", err);
    }
  };

  const onClose = () => {
    reset(defaultValues);
    setSubmitStatus(ESubmitStatus.IDLE);
    toggleModal();
  };

  /** Refactor: Extract modal content rendering to a function for SonarQube */
  const renderModalContent = () => {
    if (submitStatus === ESubmitStatus.SUCCESS) {
      return (
        <div className="flex h-[85vh] flex-col items-center justify-center p-10">
          <RichtextRenderer
            content={thank_you_message_text as string}
            customStyle={{
              "": {
                textAlign: "center",
                color: "var(--text-base-green)",
              },
            }}
          />
        </div>
      );
    }
    if (submitStatus === ESubmitStatus.LOADING) {
      return (
        <div className="flex h-[85vh] w-full items-center justify-center p-10">
          <Spinner
            className="h-10 w-10 animate-spin fill-[var(--text-base-primary)] text-gray-200 dark:text-gray-600"
            fill={"var(--fill-base-main)"}
          />
        </div>
      );
    }
    if (submitStatus === ESubmitStatus.ERROR) {
      return (
        <div className="flex h-[85vh] flex-col items-center justify-center p-10">
          <p className="text-center text-[var(--text-base-red)]">
            メッセージの送信中にエラーが発生しました
          </p>
        </div>
      );
    }
    return (
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="scrollbar-banquet-form flex h-full flex-col items-center px-4 md:px-20"
        >
          <ContactForm formLabel={formLabel} listOption={listOption} />
          <MeetingEventForm formLabel={formLabel} submitStatus={submitStatus} />

          <Button type="submit" size={"lg"} className="mt-2.5 w-fit">
            {submit_label || ""}
          </Button>
          <p className="px-4 pt-6 pb-7 text-sm font-[var(--font-light)] text-[var(--text-gray-normal)]">
            {free_quotation_text || ""}
          </p>
        </form>
      </FormProvider>
    );
  };

  return (
    <Modal
      open={isOpen}
      toggleModal={onClose}
      closeClassName="bg-[var(--fill-base-main)] text-white opacity-100 transition-opacity hover:opacity-70 rounded-full p-1.5"
      containerClassName="rounded-2xl !max-w-[343px] gap-0 md:!max-w-[660px]"
      footerClassName="!justify-center bg-[var(--fill-gray-lighter)] rounded-b-2xl"
      footer={
        terms_and_conditions_text ? (
          <div className="p-4 md:px-9 md:py-4">
            <RichtextRenderer
              content={terms_and_conditions_text || ""}
              customStyle={{
                "": {
                  textAlign: "center",
                },

                p: {
                  marginBottom: "0",
                },
                font: {
                  fontWeight: "var(--font-semibold) !important",
                  color: "var(--text-base-primary) !important",
                },
              }}
            />
          </div>
        ) : (
          <></>
        )
      }
      header={<></>}
      title=""
      trigger={
        customTrigger ?? (
          <Button
            hierarchy={hierarchyTriggerBtn}
            variant={variantTriggerBtn}
            size={sizeTriggerBtn}
            className={classNameTriggerBtn}
          >
            {contactBtnIcon && <i className={`h-4 w-4 ${contactBtnIcon}`}></i>}
            {contactBtn}
          </Button>
        )
      }
    >
      <div className="hide-scrollbar max-h-[85vh] overflow-hidden py-12 md:px-1">
        {renderModalContent()}
      </div>
    </Modal>
  );
};

export default ContactFormModal;
